import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface CopyToYearsDialogProps {
  open: boolean;
  /** All available years from Settings (strings, descending). */
  availableYears: string[];
  /** The year currently being edited — excluded from the selectable list. */
  currentYear: string;
  /** Whether a save/copy operation is in progress. */
  isSaving?: boolean;
  onConfirm: (selectedYears: string[]) => void;
  onCancel: () => void;
}

const CopyToYearsDialog: React.FC<CopyToYearsDialogProps> = ({
  open,
  availableYears,
  currentYear,
  isSaving = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();

  // Selectable years = all available years minus the current one
  const selectableYears = useMemo(
    () => availableYears.filter((y) => y !== currentYear),
    [availableYears, currentYear],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Reset selection whenever the dialog opens
  useEffect(() => {
    if (open) {
      setSelected(new Set());
    }
  }, [open]);

  const handleToggle = useCallback((year: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(year)) {
        next.delete(year);
      } else {
        next.add(year);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelected(new Set(selectableYears));
  }, [selectableYears]);

  const handleClearAll = useCallback(() => {
    setSelected(new Set());
  }, []);

  const handleConfirm = () => {
    onConfirm(Array.from(selected).sort((a, b) => Number(b) - Number(a)));
  };

  const allSelected = selected.size === selectableYears.length && selectableYears.length > 0;
  const noneSelected = selected.size === 0;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      data-testid="copy-to-years-dialog"
    >
      <DialogTitle>
        {t('userPreferences.copyToYearsDialog.title', 'Copy preferences to other years')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t(
            'userPreferences.copyToYearsDialog.message',
            'Select the years to which you want to copy the preferences from {{year}}. This will overwrite any existing preferences for the selected years and save immediately.',
            { year: currentYear },
          )}
        </DialogContentText>

        {/* Select All / Clear All controls */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Button
            size="small"
            variant="text"
            onClick={handleSelectAll}
            disabled={allSelected}
          >
            {t('userPreferences.copyToYearsDialog.selectAll', 'Select all')}
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={handleClearAll}
            disabled={noneSelected}
          >
            {t('userPreferences.copyToYearsDialog.clearAll', 'Clear all')}
          </Button>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Year checkboxes */}
        <FormGroup sx={{ maxHeight: 300, overflowY: 'auto' }}>
          {selectableYears.map((year) => (
            <FormControlLabel
              key={year}
              control={
                <Checkbox
                  checked={selected.has(year)}
                  onChange={() => handleToggle(year)}
                  size="small"
                />
              }
              label={year}
            />
          ))}
          {selectableYears.length === 0 && (
            <DialogContentText sx={{ py: 2, textAlign: 'center' }}>
              {t(
                'userPreferences.copyToYearsDialog.noYears',
                'No other years available to copy to.',
              )}
            </DialogContentText>
          )}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isSaving}>
          {t('userPreferences.copyToYearsDialog.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={noneSelected || isSaving}
        >
          {isSaving
            ? t('userPreferences.copyToYearsDialog.copying', 'Copying...')
            : t('userPreferences.copyToYearsDialog.confirm', 'Copy')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyToYearsDialog;

